import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { Upload, Search, Download, Trash2, Edit2 } from 'lucide-react';
import FileUploadDialog from './FileUploadDialog';
import FileIcon from './FileIcon';
import FileActions from './FileActions';
import RenameFileModal from './FileStorage/RenameFileModal';
import { app } from "../../firebase";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { getAuth } from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  getFirestore,
  setDoc,
  addDoc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { FaFileAlt, FaFilePdf, FaFileWord } from 'react-icons/fa';

const getCollectionName = (organization) => {
  if (organization === "bytesplash") return "bytesplash_jobOffers";
  return `${organization}_jobOffers`;
};

const OfferLetters = ({ organization = "bytesplash" }) => {
  const [files, setFiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameLoading, setRenameLoading] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const { toast } = useToast();
  const db = getFirestore(app);
  const storage = getStorage(app);

  useEffect(() => {
    loadFiles();
    // eslint-disable-next-line
  }, [organization]);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const colRef = collection(db, getCollectionName(organization));
      
      const snapshot = await getDocs(colRef);
      setFiles(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      toast({ title: "Error", description: "Failed to load files", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (uploadedFiles) => {
    for (const file of uploadedFiles) {
      try {
        const fileData = {
          name: file.name,
          type: file.type,
          size: file.size,
          uploadDate: new Date().toISOString().split('T')[0],
          organization,
          url: null
        };
        const storageRef = ref(storage, `${getCollectionName(organization)}/${file.name}`);
        await uploadBytes(storageRef, file);
        const downloadUrl = await getDownloadURL(storageRef);
        const docRef = doc(collection(db, getCollectionName(organization)));
        await setDoc(docRef, { ...fileData, url: downloadUrl });
        toast({ title: "Success", description: `${file.name} uploaded successfully` });
      } catch (error) {
        toast({ title: "Error", description: `Failed to upload ${file.name}`, variant: "destructive" });
      }
    }
    loadFiles();
    setUploadDialogOpen(false);
  };

  const handleDelete = async () => {
    if (!fileToDelete) return;
    try {
      await deleteDoc(doc(db, getCollectionName(organization), fileToDelete.id));
      const storageRef = ref(storage, `${getCollectionName(organization)}/${fileToDelete.name}`);
      await deleteObject(storageRef);
      toast({ title: "Success", description: "File deleted successfully" });
      loadFiles();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete file", variant: "destructive" });
    } finally {
      setDeleteDialogOpen(false);
      setFileToDelete(null);
    }
  };

  const handleCopyLink = (id, url) => {
    navigator.clipboard.writeText(url);
    setCopiedLinkId(id);
    toast({ title: "Copied", description: "Download link copied to clipboard" });
    setTimeout(() => setCopiedLinkId(null), 1500);
  };

  const handleRenameFile = (file) => {
    setRenameTarget(file);
  };

  const confirmRenameFile = async (newName) => {
    if (!renameTarget) return;
    setRenameLoading(true);
    try {
      const oldFileRef = ref(storage, `${getCollectionName(organization)}/${renameTarget.name}`);
      const newFileRef = ref(storage, `${getCollectionName(organization)}/${newName}`);
      
      // Download the file from old location
      const response = await fetch(renameTarget.url);
      const blob = await response.blob();
      
      // Upload to new location
      await uploadBytes(newFileRef, blob);
      const newDownloadUrl = await getDownloadURL(newFileRef);
      
      // Update Firestore document with new name and download URL
      await updateDoc(doc(db, getCollectionName(organization), renameTarget.id), {
        name: newName,
        url: newDownloadUrl,
        uploadDate: new Date().toISOString().split('T')[0],
      });
      
      // Delete old file from storage
      try {
        await deleteObject(oldFileRef);
      } catch (e) {
        console.log("Could not delete old file:", e);
      }
      
      setRenameTarget(null);
      loadFiles();
      toast({ title: "Success", description: `File renamed to ${newName}` });
    } catch (e) {
      alert("Failed to rename file: " + e.message);
      console.error("Rename error:", e);
    }
    setRenameLoading(false);
  };

  const filteredFiles = files.filter(file => file.name.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold">Offer Letters</h2>
          <p className="text-sm sm:text-base text-muted-foreground">{organization === 'bytesplash' ? 'ByteSplash' : 'Margey Labs'} • {files.length} files</p>
        </div>
          <Button
            onClick={() => setUploadDialogOpen(true)}
            className="gap-2 w-full sm:w-auto bg-[#0071bd] text-white hover:bg-[#005fa3] rounded-xl px-5 py-2 flex items-center justify-center shadow-sm"
            style={{ borderRadius: '14px' }}
          >
            <Upload className="h-5 w-5 mr-2" />
            <span className="font-semibold">Upload Files</span>
          </Button>
      </div>
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <CardTitle className="text-lg sm:text-xl">File Management</CardTitle>
            <div className="relative w-full sm:w-64 group">
              <Input
                placeholder="Search files..."
                className="pr-11 pl-3 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#0071bd] focus:border-[#0071bd] transition-all duration-200 shadow-sm text-gray-800 bg-white"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ fontSize: '1rem', fontWeight: 500, letterSpacing: '0.01em' }}
                onFocus={e => e.target.parentElement?.classList.add('input-focused')}
                onBlur={e => e.target.parentElement?.classList.remove('input-focused')}
              />
              <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[#0071bd] transition-all duration-200 group-[.input-focused]:text-[#005fa3] group-[.input-focused]:right-3">
                <Search className="h-5 w-5" />
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredFiles.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchQuery ? 'No files match your search' : 'No files uploaded yet'}
            </div>
          ) : (
            <div className="grid gap-3 sm:gap-4">
              {filteredFiles.map((file) => (
                  <div key={file.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 sm:p-4 border rounded-lg hover:bg-blue-50 transition-colors gap-3">
                    <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                        {file.name.toLowerCase().endsWith('.pdf') ? <FaFilePdf className="h-6 w-6 text-red-500" /> :
                          file.name.toLowerCase().endsWith('.doc') || file.name.toLowerCase().endsWith('.docx') ? <FaFileWord className="h-6 w-6 text-blue-600" /> :
                          file.type.startsWith("image/") ? <FaFileImage className="h-6 w-6 text-green-500" /> :
                          file.type === "text/plain" || file.name.endsWith(".txt") ? <FaFileAlt className="h-6 w-6 text-gray-600" /> :
                          <FaFileAlt className="h-6 w-6 text-gray-400" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-medium text-base truncate text-[#0071bd]">{file.name}</h4>
                        <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-600">
                          <span>{(file.size / 1024).toFixed(2)} KB</span>
                          <span className="hidden sm:inline">•</span>
                          <span>{file.uploadDate}</span>
                          <span className="hidden sm:inline">•</span>
                          <span>by {file.createdBy || 'unknown'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="capitalize text-xs bg-gray-100 text-gray-800 border-none">Document</Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-yellow-100"
                        aria-label="Rename"
                        onClick={() => handleRenameFile(file)}
                        title="Rename file"
                      >
                        <Edit2 className="h-5 w-5 text-yellow-600" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-blue-100"
                        aria-label="Download"
                        onClick={() => window.open(file.url, '_blank')}
                      >
                        <Download className="h-5 w-5 text-[#0071bd]" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-red-100"
                        aria-label="Delete"
                        onClick={() => { setFileToDelete(file); setDeleteDialogOpen(true); }}
                      >
                        <Trash2 className="h-5 w-5 text-red-500" />
                      </Button>
                    </div>
                  </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <FileUploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        onFileUpload={handleFileUpload}
      />
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete File</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            Are you sure you want to delete <span className="font-semibold">{fileToDelete?.name}</span>? This action cannot be undone.
          </div>
          <DialogFooter className="flex flex-row gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => { setDeleteDialogOpen(false); setFileToDelete(null); }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Rename File Modal */}
      {renameTarget && (
        <RenameFileModal
          open={!!renameTarget}
          onOpenChange={(open) => {
            if (!open) setRenameTarget(null);
          }}
          file={renameTarget}
          onConfirm={confirmRenameFile}
          loading={renameLoading}
        />
      )}
    </div>
  );
};

export default OfferLetters;
